/* eslint-disable max-len */
/* eslint-disable new-cap */
import {graphql} from 'gatsby';
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews';
import PropTypes from 'prop-types';
import React from 'react';

import {Search} from '../components/landingpage_slices';
import '../components/layout/ContentLayout.css';
import Layout from '../components/layout/layout';
import {
  ActionCall,
  Author,
  Collection,
  Destination,
  Divider,
  Feature,
  Hero,
  Image,
  ImageCollection,
  Instagram,
  Line,
  Quote,
  SectionHeader,
  Text,
  Video,
} from '../components/slices';
import {
  AuthorFragment,
  CollectionFragment,
  DestinationFragment,
  FeatureFragment,
  HeroFragment,
  ImageCollectionFragment,
  ImageFragment,
  InstagramFragment,
  QuoteFragment,
  SectionHeaderFragment,
  TextFragment,
  VideoFragment,
  TemaHeroFragment,
} from '../fragments';
import {getDefaultMeta} from '../utils/defaultMeta';
import {sortByDate} from '../utils/sortByDate';

const Content = ({data}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filteredArr, setFilteredArr] = React.useState(data.content.edges);
  const [currentLang, setCurrentLang] = React.useState(data.prismicContent.lang);
  const meta = getDefaultMeta(data.prismicContent.data, currentLang, data.prismicContent.uid);

  React.useEffect(() => {
    const arr = data.content.edges;
    const themes = data.allPrismicUpplevelse.edges;
    const tagsContent = arr.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang && JSON.stringify(post.node.tags).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const tagsThemes = themes.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang && JSON.stringify(post.node.tags).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const titleContent = arr.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang &&
        JSON.stringify(post.node?.dataRaw.title).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const titleThemes = themes.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang &&
        JSON.stringify(post.node.dataRaw.titel).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const postContent = arr.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang &&
        JSON.stringify(post.node.dataRaw.body).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const postThemes = themes.filter(
        (post) =>
          post.node.lang === data.prismicContent.lang &&
        JSON.stringify(post.node.dataRaw.body).toLowerCase().match(searchTerm.toLowerCase()),
    );
    const tags = [...new Set([...tagsContent, ...tagsThemes])];
    sortByDate(tags);
    const titles = [...new Set([...titleContent, ...titleThemes])];
    sortByDate(titles);
    const post = [...new Set([...postContent, ...postThemes])];
    sortByDate(post);
    let searchResults = [...new Set([...tags, ...titles, ...post])];

    searchResults = searchResults.filter((post) => !JSON.stringify(post).toLowerCase().match('osökbar'));
    searchResults.splice(20, searchResults.length - 20);

    setFilteredArr(searchResults);
  }, [searchTerm, data.content.edges, data.prismicContent.lang, data.allPrismicUpplevelse.edges]);

  const drawSlices = (slice) => {
    switch (slice.slice_type) {
      case 'hero':
        return <Hero slice={slice} key={slice.id} />;
      case 'feature':
        return <Feature slice={slice} key={slice.id} />;
      case 'text':
        return <Text slice={slice} key={slice.id} />;
      case 'image':
        return <Image slice={slice} key={slice.id} />;
      case 'collection':
        return <Collection slice={slice} key={slice.id} />;
      case 'section_header':
        return <SectionHeader slice={slice} key={slice.id} />;
      case 'quote':
        return <Quote slice={slice} key={slice.id} />;
      case 'video':
        return <Video slice={slice} key={slice.id} />;
      case 'instagram':
        return <Instagram slice={slice} key={slice.id} />;
      case 'divider':
        return <Divider slice={slice} key={slice.id} />;
      case 'image_collection':
        return <ImageCollection slice={slice} key={slice.id} />;
      case 'author':
        return <Author slice={slice} key={slice.id} />;
      case 'line':
        return <Line slice={slice} key={slice.id} />;
      case 'destination_grid':
        return <Destination slice={slice} key={slice.id} />;
      case 'call_to_action':
        return <ActionCall slice={slice} key={slice.id} />;
      default:
        return null;
    }
  };

  const contentData = data.prismicContent.data;

  return (
    <Layout
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      meta={meta}
    >
      {searchTerm === '' ? (
        contentData.body.map((slice) => drawSlices(slice))
      ) : (
        <Search filteredArr={filteredArr} searchTerm={searchTerm} currentLang={currentLang} />
      )}
    </Layout>
  );
};

export const query = graphql`
  query getContent($uid: String) {
    prismicContent(uid: {eq: $uid}) {
      _previewable
      data {
        body {
          ...CollectionFragment
          ...HeroFragment
          ...FeatureFragment
          ...TextFragment
          ...ImageFragment
          ...VideoFragment
          ...QuoteFragment
          ...SectionHeaderFragment
          ...InstagramFragment
          ...DestinationFragment
          ...ImageCollectionFragment
          ...CallToActionFragment
          ... on PrismicContentDataBodyDivider {
            slice_type
          }
          ... on PrismicContentDataBodyLine {
            slice_type
          }
          ...AuthorFragment
        }
      }
      uid
      lang
    }
    content: allPrismicContent {
      edges {
        node {
          uid
          dataRaw
          _previewable
          data {
            title
            body {
              ...HeroFragment
            }
            taggar {
              text
            }
          }
          lang
          last_publication_date
          tags
        }
      }
      totalCount
    }
    allPrismicUpplevelse {
      edges {
        node {
          uid
          dataRaw
          last_publication_date
          tags
          lang
          _previewable
          data {
            titel {
              text
            }
            body {
              ...TemaHeroFragment
            }
          }
        }
      }
    }
  }
`;

Content.fragments = [
  HeroFragment,
  ImageFragment,
  FeatureFragment,
  TextFragment,
  CollectionFragment,
  SectionHeaderFragment,
  QuoteFragment,
  InstagramFragment,
  VideoFragment,
  ImageCollectionFragment,
  AuthorFragment,
  DestinationFragment,
  TemaHeroFragment,
];

Content.propTypes = {
  data: PropTypes.any,
};

export default withPrismicPreview(Content, [
  {
    repositoryName: process.env.PRISMIC_REPOSITORY_NAME,
    linkResolver: require('../utils/linkResolver').linkResolver,
  },
]);
